import type { TokenResponse } from '@react-oauth/google';

import { useMainContext } from 'routes/MainContextProvider';
import catchErrorAndCloseLoading from 'functions/general/catchErrorAndCloseLoading';
import getMessageFromError from 'functions/general/getMessageFromError';
import ModalSignUpLarge from './ModalSignUpLarge';
import { register as registerSuccess } from 'store/reducers/auth';
import { close as setCloseSignUp } from 'store/reducers/signUp';
import { ACCOUNT_TYPES, type IntUserResponse } from 'services/local/types';
import type { GmailSignInData, SignUpData } from 'services/http/types';

const ModalSignUpLargeDispatcher = () => {
  const {
    api,
    local,
    toastr,
    history,
    dispatch,
    scrollToTop,
    openLoading,
    closeLoading,
    signUp
  } = useMainContext();

  const onSignUp = async (data: SignUpData) => {
    const state = local.integrationsWebSite.getWebsite();
    if (state) {
      data.state = state.state;
      data.account_type = ACCOUNT_TYPES.analytics;
    }
    return api.auth.signUp(data).then(response => {
      toastr.success('', 'user created, please check your email to confirm');
      return Promise.resolve(response);
    }).catch((error: unknown) => {
      const message = getMessageFromError(error);
      toastr.error(message, '');
      if (error instanceof Error) return Promise.reject(error);
      return Promise.reject(new Error(message));
    });
  }

  const onSignUpGmail = async (googleUser: TokenResponse): Promise<IntUserResponse> => {
    const id_token = googleUser.access_token;
    const reportId = local.analysis.getLastFullReportId();
    const state = local.integrationsWebSite.getWebsite();
    const data: GmailSignInData = {
      token: id_token,
    };
    if (state) {
      data.state = state.state;
      data.account_type = ACCOUNT_TYPES.analytics;
    }
    if (reportId) {
      data.report_id = reportId
    }
    return api.auth.gmailSignIn(data);
  };

  const onClose = () => {
    dispatch(setCloseSignUp());
  };

  const onSend = (data: SignUpData) => {
    openLoading('Creating account...');
    const reportId = local.analysis.getLastFullReportId();
    data.report_id = null;
    if (reportId) {
      data.report_id = reportId
    }
    return onSignUp(data).then(() => {
      closeLoading();
      scrollToTop();
      onClose();
      if (reportId) {
        history('/');
      }
      return Promise.resolve();
    }).catch(catchErrorAndCloseLoading(closeLoading));
  };

  const onSendGmail = (googleUser: TokenResponse) => {
    openLoading('Creating account...');
    return onSignUpGmail(googleUser).then(response => {
      local.auth.setUser(response);
      dispatch(registerSuccess(response))
      closeLoading();
      scrollToTop();
      if (local.analysis.getLastFullReportId()) {
        history('/documents/my-documents/1');
      }
      return Promise.resolve();
    }).catch(catchErrorAndCloseLoading(closeLoading));
  };
  if (!signUp) return null;
  return (
    <ModalSignUpLarge
      onClose={onClose}
      onSend={onSend}
      onSendGmail={onSendGmail} />
  );
};

export default ModalSignUpLargeDispatcher;
